'use strict'
import Decimal from 'decimal.js'

/**
 * 数値をカンマ区切りの数字に変換
 *
 * @param {String} val 値
 * @param {Number} numDecPlaces 少数桁数
 * @returns 変換後数時
 */
export const formatCommaDigit = (val, numDecPlaces = 0) => {
  if (!val || val === '') {
    return val
  }
  //入力値を数値だけ半角変換
  let halfWidthVal = String(val).replace(/[０-９]/g, function (x) {
    return String.fromCharCode(x.charCodeAt(0) - 0xfee0)
  })
  //カンマを除去（入力値が数値か否かを確認のため）
  let commaRemovedVal = halfWidthVal.replaceAll(',', '').replaceAll('、', '')
  if (isNaN(commaRemovedVal)) {
    //数値でなければ空白にする
    return ''
  } else {
    //入力値をカンマ入り数字テキストに再変換
    return new Intl.NumberFormat('ja-JP', {
      maximumFractionDigits: numDecPlaces,
      minimumFractionDigits: 0, // 小数点以下がなければ表示しない
      roundingMode: 'trunc' // 切り捨て
    }).format(commaRemovedVal)
  }
}

/**
 * カンマ区切りの数字を数値に変換
 *
 * @param {String} val 値
 * @returns 変換後数値
 */
export const parseCommaDigit = (val) => {
  if (!val || val === '') {
    return ''
  }
  return val.replaceAll(',', '')
}

/**
 * 入力値を指定されたルールに従ってフォーマットし、数値を返却。
 *
 * @param {string} val - フォーマットする値
 * @param {number} [numDecPlaces=0] - 切り捨てる小数点以下の桁数。デフォルトは0. Default is `0`
 * @param {boolean} [allowNegative=false] - 負数を許可するかどうか。デフォルトはfalse. Default is `false`
 * @returns {number | string} - 処理後の数値、または無効な場合は空文字。
 */
export const formatNumberValue = (val, numDecPlaces = 0, allowNegative = false) => {
  // 入力値を文字列変換
  const value = String(val)
  // 空値の場合はnullを返す
  if (!value || value.trim() === '') {
    return ''
  }

  // 全角数字を半角に変換し、カンマと空白を除去
  const halfWidthVal = String(value).replace(/[０-９]/g, (x) => String.fromCharCode(x.charCodeAt(0) - 0xfee0))
  const cleanedVal = halfWidthVal.replace(/[,、\s]/g, '')

  // 数値以外の場合はnullを返す
  if (isNaN(cleanedVal)) {
    return ''
  }

  const numberValue = parseFloat(cleanedVal)

  // 負数が許可されいない場合に値が負数の場合はnullを返す
  if (!allowNegative && numberValue < 0) {
    return ''
  }

  // 指定された桁数で切り捨て
  const factor = Math.pow(10, numDecPlaces)
  const truncatedValue = Math.trunc(numberValue * factor) / factor

  return truncatedValue
}

/**
 * KPI数値変換
 *
 * @param {String} val 値
 * @param {String} unit KPI単位
 * @param {String} defaultVal 値無し時の返却値（省略時"-"）
 */
export const formatKpiNumber = (val, unit, defaultVal) => {
  if (val !== 0 && !val) {
    return defaultVal ? defaultVal : '-'
  }
  if (unit === 'yen') {
    const numLen = parseInt(val).toString().length
    if (numLen <= 6) {
      // 6桁以内
      return '\xA5' + formatCommaDigit(Math.round(val * 0.001 * 10) / 10, 1) + 'K'
    } else if (numLen <= 9) {
      // 9桁以内
      return '\xA5' + formatCommaDigit(Math.round(val * 0.000001 * 10) / 10, 1) + 'M'
    } else if (numLen <= 23) {
      // 12桁以内
      return '\xA5' + formatCommaDigit(Math.round(val * 0.000000001 * 10) / 10, 1) + 'B'
    } else {
      // 13桁以上
      return '\xA5' + formatCommaDigit(Math.round(val * 0.000000000001 * 10) / 10, 1) + 'T'
    }
  }
  if (unit === 'hour') {
    return formatCommaDigit(Math.round(val * 10) / 10, 1) + 'h'
  }
  if (unit === 'percent') {
    return formatCommaDigit(Math.round(val * 10) / 10, 1) + '%'
  }
  if (unit === 'times') {
    return formatCommaDigit(val, 1) + '回'
  }
  if (unit === 'items') {
    return formatCommaDigit(val, 1) + '件'
  }
  if (unit === 'people') {
    return formatCommaDigit(val, 1) + '人'
  }
  return val
}

/**
 * 2つの数値を演算し、指定された小数点以下の桁数で四捨五入 ※浮動小数点の演算誤差回避
 *
 * @param {string | number} value1 最初の値
 * @param {string | number} value2 2番目の値
 * @param {string} operator 演算子 ('+', '-', '*', '/')
 * @param {number | undefined} decimalPlaces 小数点以下の桁数
 * @returns {string} フォーマットされた計算結果
 */
export const calculateBinary = (value1, value2, operator, numDecPlaces) => {
  const d1 = new Decimal(value1 || 0)
  const d2 = new Decimal(value2 || 0)
  let result

  switch (operator) {
    case '+':
      result = d1.plus(d2)
      break
    case '-':
      result = d1.minus(d2)
      break
    case '*':
      result = d1.times(d2)
      break
    case '/':
      result = d1.dividedBy(d2)
      break
    default:
      throw new Error('無効な演算子が指定されました。')
  }

  // 小数点桁数が指定されている場合のみtoFixedを適用
  if (numDecPlaces !== undefined && numDecPlaces !== null) {
    return result.toFixed(numDecPlaces)
  } else {
    // 桁数指定がない場合はそのまま文字列として返す
    return result.toString()
  }
}
